// AppShell — root navigator for the web app.
//
// Replaces the legacy `app-shell.jsx` with a responsive layout: sidebar +
// main on desktop (≥900px), top bar + bottom tab bar on mobile. The
// iPhone-frame chrome and the Tweaks panel from the prototype are gone —
// theme/accent/lang switching moves to the Settings screen when it
// migrates.
//
// State persisted in localStorage: language, theme mode, accent, density,
// last route. Auth gate redirects to onboarding when signed-out and to
// DEFAULT_ROUTE (translator) when sign-in completes.
//
// Which routes exist at all is decided by the service registry
// (`./services` + `./services-context`): screens whose backend isn't
// running are still imported here but never reachable — not from the
// nav, not from localStorage, not from `?route=`.
//
// F-01 made that decision a runtime one. `sa_route` now stores the
// user's *intent* (any syntactically valid route) and the route we
// actually render is derived on every render from the live config:
//
//     route = resolveRoute(services, storedRoute, me.role)
//
// So a route whose service is switched off falls back to DEFAULT_ROUTE
// without a reload, and — the reason for deriving rather than
// overwriting — the moment its service is switched back on, the user
// lands back where they were instead of having had their stored route
// silently rewritten to 'translator'.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ACCENTS,
  DENSITY,
  FONT_STACK,
  fullFontStack,
  HOME_VARIANTS,
  THEMES,
  type AccentKey,
  type DensityKey,
  type HomeVariantKey,
  type ThemeName,
} from '@nayto/theme';
import { isRTL, loadLang, tFn, type Lang } from '@nayto/i18n';
import { apiFetch, refreshMe, useAuthSession, useMe } from '@/auth';
import {
  DEFAULT_ROUTE,
  deepLinkRoute,
  isRoute,
  migrateStoredRoute,
  resolveRoute,
  stripRouteDeepLinkParams,
  type Route,
} from '@/services';
import { useServices, useVisibleRoutes } from '@/services-context';
import { Avatar, Icon } from '@/components/icons';
import { useLocalStorageState } from '@/hooks/useLocalStorageState';
import { onUsersChanged } from '@/utils/app-events';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { ScreenBoundary, lazyScreen } from '@/lazy-screen';
import { detectInitialLang, isLang } from '@/lang-boot';

// ─── Eager vs. lazy (F-08) ───────────────────────────────────────────
//
// Eager = the screens that can BE the first paint. Everything else is
// reached by a click, and a click can afford a chunk.
//
// For a signed-in reader that is the default route (translator); for
// everyone else it is one of the two gates (onboarding, pending
// approval). Making those lazy would put a loading state in front of
// every cold start — paying, on the one render nobody can avoid, to
// save bytes on renders most people never reach.
//
// Everything else is a chunk, including the admin panel. It is 87 kB —
// the largest single screen in the app — and exactly one person opens
// it. That is the clearest trade in the file.
import { Onboarding } from '@/screens/Onboarding';
import { PendingApprovalScreen } from '@/screens/PendingApprovalScreen';
import { TranslatorScreen } from '@/screens/TranslatorScreen';

const HomeScreen = lazyScreen(() => import('@/screens/HomeScreen'), 'HomeScreen');
const RepliesScreen = lazyScreen(() => import('@/screens/RepliesScreen'), 'RepliesScreen');
const LiveScreen = lazyScreen(() => import('@/screens/LiveScreen'), 'LiveScreen');
const CompetitorsScreen = lazyScreen(
  () => import('@/screens/CompetitorsScreen'),
  'CompetitorsScreen',
);
const ContentIdeasScreen = lazyScreen(
  () => import('@/screens/ContentIdeasScreen'),
  'ContentIdeasScreen',
);
const SettingsScreen = lazyScreen(() => import('@/screens/SettingsScreen'), 'SettingsScreen');
const AdminPanel = lazyScreen(() => import('@/screens/AdminPanel'), 'AdminPanel');
const MessageBoxScreen = lazyScreen(
  () => import('@/screens/MessageBoxScreen'),
  'MessageBoxScreen',
);
const PostDetailScreen = lazyScreen(
  () => import('@/screens/PostDetailScreen'),
  'PostDetailScreen',
);
const StatsScreen = lazyScreen(() => import('@/screens/StatsScreen'), 'StatsScreen');
const LeaderboardScreen = lazyScreen(
  () => import('@/screens/reports/LeaderboardScreen'),
  'LeaderboardScreen',
);
const ControlScreen = lazyScreen(() => import('@/screens/ControlScreen'), 'ControlScreen');

// ─── Routes ──────────────────────────────────────────────────────────
//
// `Route`, KNOWN_ROUTES, ROLE_ROUTES and the enabled-set live in
// `./services` so the guard logic is unit-testable without React.

const isThemeName = (s: string): s is ThemeName => s === 'light' || s === 'dark';
const isAccent = (s: string): s is AccentKey =>
  s === 'amber' || s === 'coral' || s === 'electric' || s === 'emerald' || s === 'violet';
const isDensity = (s: string): s is DensityKey => s === 'compact' || s === 'comfortable';

// ─── Component ───────────────────────────────────────────────────────

export function AppShell() {
  // The fallback is the same guess main.tsx made before the first
  // render, so the dictionary already in memory is the one this resolves
  // to. Passing 'en' here would have made a Hebrew browser render English
  // for one frame and then re-render.
  const [lang, setLangRaw] = useLocalStorageState<Lang>('sa_lang', detectInitialLang(), isLang);

  /**
   * Changing language is now a fetch, so it lands in two steps: get the
   * dictionary, THEN switch. Doing it the other way round renders one
   * frame of raw keys, which is the single thing the split must never
   * cost a reader.
   *
   * A failed fetch leaves the language where it was. The click appears
   * not to take, which is unhelpful but honest — and the alternative,
   * switching anyway, means a screen of `chQuotaPerDay`.
   */
  const setLang = useCallback(
    (next: Lang) => {
      if (next === lang) return;
      void loadLang(next).then(
        () => setLangRaw(next),
        () => undefined,
      );
    },
    [lang, setLangRaw],
  );
  const [themeMode] = useLocalStorageState<ThemeName>('sa_theme', 'light', isThemeName);
  const [accentKey] = useLocalStorageState<AccentKey>('sa_accent', 'electric', isAccent);
  const [densityKey] = useLocalStorageState<DensityKey>('sa_density', 'comfortable', isDensity);

  const theme = THEMES[themeMode];
  const accent = ACCENTS[accentKey];
  const density = DENSITY[densityKey];
  const t = tFn(lang);
  const rtl = isRTL(lang);

  // 768px = iPad portrait. The previous 900px threshold pushed iPads
  // into the mobile shell (tab bar instead of sidebar) — the 240px
  // sidebar fits comfortably at 768px and is the more useful nav at
  // tablet width.
  const isDesktop = useMediaQuery('(min-width: 768px)');

  // Read URL deep-links once on mount: `?variant=`, `?route=`, and the
  // retired `?report=` (F-16: every value it ever carried now names a
  // route of its own). They pin the initial Home variant or route so QA
  // and share-links work without a UI control. `?route=X` *overrides*
  // the persisted localStorage route — without that override, the
  // localStorage value silently wins on every load and the deep-link
  // appears ignored.
  const initialDeepLinks = useMemo(() => {
    const qs = new URLSearchParams(window.location.search);
    const variantParam = qs.get('variant');
    return {
      variant:
        variantParam != null && variantParam in HOME_VARIANTS
          ? (variantParam as HomeVariantKey)
          : 'control',
      // Deep links are only parsed here, never gated here: whether the
      // target is reachable is decided on every render against the live
      // config (`route` below). Gating at mount would freeze the answer
      // to whatever the config was in the first 200ms, before GET
      // /api/services had replied.
      //
      // Retired names map forward, so `?route=reports` and
      // `?report=leaderboard` land on the leaderboard, not the translator.
      route: deepLinkRoute(qs),
    } satisfies {
      variant: HomeVariantKey;
      route: Route | null;
    };
  }, []);

  // A `sa_route` stored before F-16 may say `reports` or `report`. Map it
  // forward BEFORE the storage hook below reads it — that hook falls back
  // to its default on anything `isRoute` rejects, which would quietly
  // send a user who left the app on the leaderboard to the translator.
  // Lazy state initialiser: runs once, during the first render, ahead of
  // the hook that follows it.
  useState(() => {
    try {
      migrateStoredRoute(window.localStorage);
    } catch {
      /* storage blocked — nothing stored, nothing to migrate */
    }
    return null;
  });

  // The live service config. Starts at the static default (translator +
  // settings + admin) and is replaced only by a config the provider
  // successfully read from GET /api/services.
  const services = useServices();

  // `sa_route` holds INTENT — validated for shape only (`isRoute`), not
  // for reachability. Reachability is applied below, on every render,
  // against the live config. Storing the resolved value instead would
  // destroy the user's place the first time a service was briefly off.
  const [storedRoute, setRoute] = useLocalStorageState<Route>(
    'sa_route',
    initialDeepLinks.route ?? 'onboarding',
    isRoute,
  );
  const [postId, setPostId] = useState<string | null>(null);

  // Apply the deep-link on mount even when localStorage already has a
  // sa_route value — the URL is the most recent intent.
  useEffect(() => {
    if (initialDeepLinks.route) setRoute(initialDeepLinks.route);
    // Applied once — now drop the params from the address bar so a later
    // refresh (or a bookmark of the current page) honours the persisted
    // route instead of snapping back to the deep-linked one. Without
    // this, a share link's `?route=translator` made every refresh from
    // Settings land on the translator (QA finding).
    try {
      const stripped = stripRouteDeepLinkParams(window.location.href);
      if (stripped) window.history.replaceState(null, '', stripped);
    } catch {
      /* ignore — some embedded contexts block history rewrites */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Auth gate ─────────────────────────────────────────────────────
  // Single source of truth for "is the user logged in": Firebase via
  // useAuthSession. When signed-out, force onboarding. When a persisted
  // session is RESTORED (loading → ready) while the route is still
  // onboarding, advance to DEFAULT_ROUTE. A sign-in performed by the
  // wizard itself (signed-out → ready, right after the OTP) must NOT
  // advance: transcriber-api answers /api/auth/session immediately, and
  // advancing here would cut the wizard short before the `lang` step —
  // Onboarding navigates via onFinish when it is done. Watch only
  // auth.status — route is the destination, not the trigger.

  const auth = useAuthSession();
  const { me } = useMe();

  // ─── Effective route ───────────────────────────────────────────────
  // The one place reachability is decided. Re-runs whenever the config
  // poll lands or the role changes, so switching a service on or off is
  // felt within a poll interval and without a reload.
  const route: Route = useMemo(
    () => resolveRoute(services, storedRoute, me?.role),
    [services, storedRoute, me?.role],
  );

  const prevAuthStatus = useRef(auth.status);
  useEffect(() => {
    const prev = prevAuthStatus.current;
    prevAuthStatus.current = auth.status;
    if (auth.status === 'signed-out' && route !== 'onboarding') {
      setRoute('onboarding');
    }
    if (auth.status === 'ready' && prev === 'loading' && route === 'onboarding') {
      setRoute(DEFAULT_ROUTE);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.status]);

  // ─── /api/me freshness pass ────────────────────────────────────────
  // /api/auth/session populates the initial profile, but role can change
  // mid-session (admin promotes a 'pending' user, flips
  // commentTrackingEnabled, etc.). Refetch /api/me once after the shell
  // mounts so the gating reflects the latest server state.
  const [meHydrated, setMeHydrated] = useState(false);
  useEffect(() => {
    if (auth.status !== 'ready' || meHydrated) return;
    void refreshMe().finally(() => setMeHydrated(true));
  }, [auth.status, meHydrated]);

  // The route guard that used to live here (an effect that rewrote
  // `sa_route` when it resolved to something else) is gone: `route`
  // above IS the guard, applied during render instead of one paint
  // later. That closes the window in which a disabled screen could
  // mount and fire a request before the effect corrected it.

  // ─── Pending-users badge ───────────────────────────────────────────
  // Admin's primary call-to-action on the Users tab is approving newly
  // registered users. Surface the count on the Admin sidebar/tab entry
  // so admins notice without having to open the tab. Refetched on route
  // change AND whenever UsersTab reports a change (approve / delete /
  // preseed / role edit) via the `nayto:users-changed` event — QA saw
  // the badge stuck at 1 after an approval until the next navigation.
  const [pendingCount, setPendingCount] = useState(0);
  const [pendingNonce, setPendingNonce] = useState(0);
  useEffect(() => onUsersChanged(() => setPendingNonce((n) => n + 1)), []);
  useEffect(() => {
    if (me?.role !== 'admin') {
      setPendingCount(0);
      return;
    }
    let cancelled = false;
    // transcriber-api reports the awaiting-approval count as
    // `pendingApprovalCount` and pins the processor-era `pendingCount`
    // (handles awaiting verification) to 0 — so prefer the former, then
    // the row count of this role=pending query, and only then the old
    // field.
    apiFetch('/api/admin/users?role=pending')
      .then((r) => (r.ok ? r.json() : Promise.resolve({})))
      .then(
        (j: { pendingApprovalCount?: number; pendingCount?: number; users?: unknown[] }) => {
          if (cancelled) return;
          const n =
            typeof j.pendingApprovalCount === 'number'
              ? j.pendingApprovalCount
              : Array.isArray(j.users)
                ? j.users.length
                : typeof j.pendingCount === 'number'
                  ? j.pendingCount
                  : 0;
          setPendingCount(n);
        },
      )
      .catch(() => {
        if (!cancelled) setPendingCount(0);
      });
    return () => {
      cancelled = true;
    };
  }, [me?.role, route, pendingNonce]);

  // ─── Document-level dir / lang sync ────────────────────────────────
  // The AppShell wrapper carries `dir` for layout, but native browser
  // features (scrollbar position, the CSS :dir() pseudo-class, screen
  // readers, third-party libs reading document.documentElement.dir)
  // need it on <html>. Same for lang — the static `lang="en"` baked
  // into apps/web/index.html doesn't reflect the user's choice; this
  // hook keeps it in sync.
  //
  // Note: there's a brief LTR flash on first load before React boots
  // for users persisted on Hebrew. Fixing that needs an inline script
  // in index.html that reads localStorage before bundle load — tracked
  // in the Phase 2 backlog.
  useEffect(() => {
    document.documentElement.lang = lang;
    document.documentElement.dir = rtl ? 'rtl' : 'ltr';
  }, [lang, rtl]);

  // ─── Navigation helpers ────────────────────────────────────────────

  const nav = (r: Route) => {
    setRoute(r);
    setPostId(null);
  };
  const openPost = (id: string) => {
    setPostId(id);
    setRoute('post');
  };

  // ─── Tab definition ────────────────────────────────────────────────
  // Full nav, then filtered by role ∩ enabled services below. Order here
  // is the display order in both sidebar and tab bar.

  const allTabs: Array<{ k: Route; icon: string; l: string }> = [
    { k: 'home', icon: 'home', l: t('home') },
    { k: 'live', icon: 'radio', l: t('live') },
    { k: 'stats', icon: 'activity', l: t('stats') },
    { k: 'replies', icon: 'message', l: t('repliesTab') },
    { k: 'leaderboard', icon: 'leaderboard', l: t('navLeaderboard') },
    { k: 'control', icon: 'clipboardCheck', l: t('navControl') },
    { k: 'competitors', icon: 'target', l: t('competitors') },
    { k: 'ideas', icon: 'sparkles', l: t('ideas') },
    { k: 'messagebox', icon: 'flag', l: t('messageBox') },
    { k: 'admin', icon: 'admin', l: t('admin') },
    { k: 'translator', icon: 'translate', l: t('translator') },
    { k: 'settings', icon: 'settings', l: t('settings') },
  ];

  // Filter the nav by the user's role ∩ the enabled services. Pending
  // users never reach this line — they hit the PendingApprovalScreen
  // branch above. For user-role we additionally hide 'replies' when
  // comment tracking is off, so they don't open a tab that just shows
  // the disabled empty state.
  const allowedRoutes = useVisibleRoutes(me?.role);
  const tabs = allTabs.filter((tb) => {
    if (!me) return false;
    if (!allowedRoutes.has(tb.k)) return false;
    if (tb.k === 'replies' && me.role === 'user' && me.commentTrackingEnabled !== true) {
      return false;
    }
    return true;
  });

  // Mobile bottom bar gets a curated 4 — Home, Live, Leaderboard, Targets
  // (Targets = the 'competitors' route per @nayto/i18n's `competitors:
  // 'Targets'`). Everything else hides behind a 5th "More" button that
  // opens a bottom sheet. Desktop sidebar stays full.
  //
  // When the curated set is thinned out by the service registry (today
  // none of the four is enabled), the bar is topped up with the next
  // visible tabs in nav order so it never degrades to a lone "More"
  // button. With every service on, the result is the original four.
  const MOBILE_CORE_ROUTES: ReadonlySet<Route> = new Set([
    'home',
    'live',
    'leaderboard',
    'competitors',
  ]);
  const MOBILE_CORE_SLOTS = 4;
  const curatedCoreTabs = tabs.filter((tb) => MOBILE_CORE_ROUTES.has(tb.k));
  const mobileCoreTabs = [
    ...curatedCoreTabs,
    ...tabs
      .filter((tb) => !MOBILE_CORE_ROUTES.has(tb.k))
      .slice(0, Math.max(0, MOBILE_CORE_SLOTS - curatedCoreTabs.length)),
  ];
  const mobileMoreTabs = tabs.filter((tb) => !mobileCoreTabs.includes(tb));
  const [moreOpen, setMoreOpen] = useState(false);
  // Close the sheet on ESC + when route changes (defensive).
  useEffect(() => {
    if (!moreOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMoreOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [moreOpen]);
  useEffect(() => {
    setMoreOpen(false);
  }, [route]);

  // Route → active tab mapping. `post` is opened from Home, so it
  // anchors there. (It used to anchor to 'reports', a leftover from when
  // post detail was reached from a report; F-16 removed the route.)
  const activeTab: Route =
    route === 'post'
      ? 'home'
      : isRoute(route) && tabs.some((tb) => tb.k === route)
        ? route
        : DEFAULT_ROUTE;

  // ─── Loading state ─────────────────────────────────────────────────
  // Brief spinner while Firebase restores any persisted session — without
  // it the app flashes the onboarding screen on every reload for users
  // who're already signed in.

  // Loading: Firebase still resolving, OR /api/me hasn't returned yet.
  // The spec is "before the response — don't render the shell, show a
  // spinner" — so we wait for both auth.status==='ready' AND meHydrated.
  // Onboarding bypasses this gate (no signed-in profile to fetch yet).
  //
  // Also while signed-out but not yet on onboarding: the auth-gate
  // effect flips the route on the next tick, and rendering the previous
  // screen for that one frame would fire its fetches with no token
  // (visible as stray 401s on sign-out).
  if (
    auth.status === 'loading' ||
    (auth.status === 'ready' && route !== 'onboarding' && !meHydrated) ||
    (auth.status === 'signed-out' && route !== 'onboarding')
  ) {
    // dir + theme color match the eventual mounted layout so the transition
    // out of the loading state doesn't flash an LTR→RTL layout shift.
    return (
      <div
        dir={rtl ? 'rtl' : 'ltr'}
        style={{
          height: '100vh',
          background: theme.bg,
          color: theme.textMuted,
          fontSize: 13,
          fontFamily: fullFontStack,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        …
      </div>
    );
  }

  // ─── Pending approval (full-bleed, no chrome) ──────────────────────
  // Pending users don't see the sidebar or any other route. Refresh +
  // sign-out are the only actions; an admin needs to flip them to a
  // real role before the shell becomes available.
  //
  // Not while onboarding is still on screen: transcriber-api creates
  // the account as `pending` on the very first /api/auth/session (right
  // after the OTP), so without this guard the wizard would jump straight
  // from `verify` to this screen and the `lang` step — which persists
  // the user's language — would never run. Onboarding hands off here via
  // onFinish once `lang` is done.

  if (auth.status === 'ready' && me?.role === 'pending' && route !== 'onboarding') {
    return <PendingApprovalScreen theme={theme} accent={accent} lang={lang} t={t} />;
  }

  // ─── Onboarding (full-bleed, no chrome) ────────────────────────────

  if (route === 'onboarding') {
    return (
      <div
        dir={rtl ? 'rtl' : 'ltr'}
        style={{
          height: '100vh',
          background: theme.bg,
          color: theme.text,
          fontFamily: fullFontStack,
          WebkitFontSmoothing: 'antialiased',
        }}
      >
        <Onboarding
          theme={theme}
          accent={accent}
          lang={lang}
          setLang={setLang}
          t={t}
          onFinish={() => nav(DEFAULT_ROUTE)}
        />
      </div>
    );
  }

  // ─── Active screen ─────────────────────────────────────────────────

  let content: React.ReactNode;
  if (route === 'home')
    content = (
      <HomeScreen
        variant={initialDeepLinks.variant}
        theme={theme}
        accent={accent}
        d={density}
        t={t}
        lang={lang}
        onOpenPost={openPost}
        onNavigate={nav}
      />
    );
  else if (route === 'replies')
    content = <RepliesScreen theme={theme} accent={accent} lang={lang} />;
  else if (route === 'leaderboard')
    content = <LeaderboardScreen theme={theme} accent={accent} lang={lang} />;
  else if (route === 'control') content = <ControlScreen theme={theme} lang={lang} />;
  else if (route === 'live')
    content = <LiveScreen theme={theme} accent={accent} lang={lang} t={t} />;
  else if (route === 'competitors')
    content = <CompetitorsScreen theme={theme} accent={accent} lang={lang} />;
  else if (route === 'ideas')
    content = <ContentIdeasScreen theme={theme} accent={accent} t={t} lang={lang} />;
  else if (route === 'settings')
    content = (
      <SettingsScreen
        theme={theme}
        accent={accent}
        lang={lang}
        setLang={setLang}
        t={t}
        onNavigate={(target) => nav(target)}
      />
    );
  else if (route === 'admin')
    content = <AdminPanel theme={theme} accent={accent} lang={lang} />;
  else if (route === 'stats')
    content = <StatsScreen theme={theme} accent={accent} lang={lang} t={t} />;
  else if (route === 'translator')
    content = <TranslatorScreen theme={theme} accent={accent} lang={lang} />;
  else if (route === 'messagebox')
    content = (
      <MessageBoxScreen theme={theme} accent={accent} lang={lang} onClose={() => nav('settings')} />
    );
  else if (route === 'post')
    content = <PostDetailScreen theme={theme} accent={accent} postId={postId} t={t} />;

  // Reference props that the placeholder screens don't yet consume — they
  // come back into play screen-by-screen during migration. Keeps eslint
  // quiet without forcing us to drop the imports.
  void accent;
  void density;
  void openPost;

  // ─── Layout ────────────────────────────────────────────────────────

  const hasBack = route === 'post';
  const title = ROUTE_TITLES[route]?.(t) ?? '';

  const sidebar = (
    <aside
      style={{
        width: 240,
        flexShrink: 0,
        background: theme.chrome,
        color: theme.chromeText,
        display: 'flex',
        flexDirection: 'column',
        padding: 16,
        boxSizing: 'border-box',
        overflow: 'auto',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '8px 10px 20px',
        }}
      >
        <img src="/logo.svg" width={32} height={32} alt="Strong Armenia" style={{ display: 'block' }} />
        <div>
          <div style={{ fontSize: 14, fontWeight: 700, letterSpacing: -0.2 }}>Strong Armenia</div>
          <div style={{ fontSize: 10, opacity: 0.6 }}>
            {auth.user?.role ? auth.user.role.charAt(0).toUpperCase() + auth.user.role.slice(1) : ''}
          </div>
        </div>
      </div>
      {tabs.map((tb) => (
        <button
          key={tb.k}
          onClick={() => nav(tb.k)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 12px',
            background: activeTab === tb.k ? 'rgba(255,255,255,0.1)' : 'transparent',
            color: activeTab === tb.k ? '#fff' : 'rgba(255,255,255,0.7)',
            border: 'none',
            borderRadius: 10,
            fontSize: 13,
            fontWeight: 500,
            cursor: 'pointer',
            marginBottom: 2,
            fontFamily: 'inherit',
            textAlign: 'start',
          }}
        >
          <Icon name={tb.icon} size={16} />
          <span style={{ flex: 1 }}>{tb.l}</span>
          {tb.k === 'admin' && pendingCount > 0 && (
            <span
              title={
                t('pendingApprovalN').replace('{n}', String(pendingCount))
              }
              style={{
                minWidth: 18,
                height: 18,
                padding: '0 6px',
                borderRadius: 9,
                background: '#dc2626',
                color: '#fff',
                fontSize: 10,
                fontWeight: 700,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: FONT_STACK.mono,
                lineHeight: 1,
              }}
            >
              {pendingCount}
            </span>
          )}
        </button>
      ))}
    </aside>
  );

  // Pending-badge target on mobile: if admin sits in the more-sheet
  // (which is the case under the curated 4-core layout), the badge
  // surfaces on the More button so admins notice without opening it.
  const moreHasAdminPending =
    mobileMoreTabs.some((t) => t.k === 'admin') && pendingCount > 0;

  const tabBarBadgeStyle = {
    position: 'absolute',
    top: 4,
    insetInlineEnd: '25%',
    minWidth: 14,
    height: 14,
    padding: '0 4px',
    borderRadius: 7,
    background: '#dc2626',
    color: '#fff',
    fontSize: 9,
    fontWeight: 700,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: FONT_STACK.mono,
    lineHeight: 1,
  } as const;

  const tabBar = (
    <nav
      style={{
        position: 'fixed',
        bottom: 0,
        insetInline: 0,
        background: theme.surface,
        borderTop: `1px solid ${theme.border}`,
        padding: '6px 6px env(safe-area-inset-bottom, 6px)',
        display: 'flex',
        gap: 2,
        zIndex: 100,
      }}
    >
      {mobileCoreTabs.map((tb) => (
        <button
          key={tb.k}
          onClick={() => nav(tb.k)}
          title={tb.l}
          style={{
            flex: 1,
            padding: '8px 4px',
            borderRadius: 10,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: activeTab === tb.k ? accent.main : theme.textSubtle,
            fontFamily: 'inherit',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
            position: 'relative',
          }}
        >
          <Icon name={tb.icon} size={20} strokeWidth={activeTab === tb.k ? 2.2 : 1.7} />
          <div style={{ fontSize: 10, fontWeight: 600 }}>{tb.l}</div>
        </button>
      ))}
      {mobileMoreTabs.length > 0 && (
        <button
          onClick={() => setMoreOpen(true)}
          title={t('moreTab')}
          aria-haspopup="menu"
          aria-expanded={moreOpen}
          style={{
            flex: 1,
            padding: '8px 4px',
            borderRadius: 10,
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color:
              moreOpen || mobileMoreTabs.some((t) => t.k === activeTab)
                ? accent.main
                : theme.textSubtle,
            fontFamily: 'inherit',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 3,
            position: 'relative',
          }}
        >
          <Icon
            name="grid"
            size={20}
            strokeWidth={
              moreOpen || mobileMoreTabs.some((t) => t.k === activeTab) ? 2.2 : 1.7
            }
          />
          <div style={{ fontSize: 10, fontWeight: 600 }}>{t('moreTab')}</div>
          {moreHasAdminPending && <span style={tabBarBadgeStyle}>{pendingCount}</span>}
        </button>
      )}
    </nav>
  );

  const moreSheet =
    moreOpen && !isDesktop ? (
      <div
        onClick={() => setMoreOpen(false)}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.55)',
          zIndex: 200,
          display: 'flex',
          alignItems: 'flex-end',
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          dir={rtl ? 'rtl' : 'ltr'}
          role="menu"
          style={{
            width: '100%',
            maxHeight: '70vh',
            background: theme.surface,
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            padding: '12px 12px calc(12px + env(safe-area-inset-bottom, 0px))',
            overflowY: 'auto',
            boxShadow: '0 -4px 20px rgba(0,0,0,0.15)',
          }}
        >
          {/* drag handle */}
          <div
            style={{
              width: 36,
              height: 4,
              background: theme.border,
              borderRadius: 2,
              margin: '0 auto 12px',
            }}
          />
          {mobileMoreTabs.map((tb) => {
            const isActive = activeTab === tb.k;
            return (
              <button
                key={tb.k}
                role="menuitem"
                onClick={() => {
                  nav(tb.k);
                  setMoreOpen(false);
                }}
                style={{
                  width: '100%',
                  padding: '12px 14px',
                  background: isActive ? theme.tint : 'transparent',
                  border: 'none',
                  borderRadius: 10,
                  color: isActive ? theme.text : theme.text,
                  fontSize: 14,
                  fontWeight: isActive ? 700 : 500,
                  cursor: 'pointer',
                  textAlign: 'start',
                  fontFamily: 'inherit',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  marginBottom: 2,
                }}
              >
                <Icon name={tb.icon} size={20} color={isActive ? accent.main : theme.textMuted} />
                <span style={{ flex: 1 }}>{tb.l}</span>
                {tb.k === 'admin' && pendingCount > 0 && (
                  <span
                    style={{
                      minWidth: 18,
                      height: 18,
                      padding: '0 6px',
                      borderRadius: 9,
                      background: '#dc2626',
                      color: '#fff',
                      fontSize: 11,
                      fontWeight: 700,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontFamily: FONT_STACK.mono,
                    }}
                  >
                    {pendingCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    ) : null;

  const topBar = (
    <header
      style={{
        flexShrink: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: isDesktop ? '14px 24px' : '14px 14px 8px',
        background: theme.surface,
        borderBottom: `1px solid ${theme.border}`,
      }}
    >
      {hasBack ? (
        <button
          onClick={() => nav('home')}
          style={{
            width: 32,
            height: 32,
            borderRadius: 16,
            background: theme.tint,
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: theme.text,
          }}
        >
          <Icon name={rtl ? 'chevronRight' : 'chevronLeft'} size={18} />
        </button>
      ) : !isDesktop ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <img src="/logo.svg" width={28} height={28} alt="Strong Armenia" style={{ display: 'block' }} />
          <div
            style={{
              fontSize: 13,
              fontWeight: 700,
              color: theme.text,
              letterSpacing: -0.2,
            }}
          >
            Strong Armenia
          </div>
        </div>
      ) : null}
      {title && (
        <div style={{ fontSize: 15, fontWeight: 600, color: theme.text, marginInlineStart: 6 }}>
          {title}
        </div>
      )}
      <div style={{ marginInlineStart: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
        {isDesktop && auth.user && (
          <Avatar
            name={auth.user.displayName ?? auth.user.email ?? auth.user.phoneNumber ?? 'User'}
            color={accent.main}
            size={32}
          />
        )}
      </div>
    </header>
  );

  return (
    <div
      dir={rtl ? 'rtl' : 'ltr'}
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: isDesktop ? 'row' : 'column',
        background: theme.bg,
        color: theme.text,
        fontFamily: fullFontStack,
        WebkitFontSmoothing: 'antialiased',
      }}
    >
      {isDesktop && sidebar}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {topBar}
        <main
          style={{
            flex: 1,
            overflow: 'auto',
            // Mobile tab bar is position:fixed (out of flow) so its
            // ~52px footprint isn't accounted for in main's layout —
            // pad the bottom of the scroll area so the last items
            // aren't hidden behind it. Desktop sidebar is in-flow
            // already; no padding needed there.
            paddingBottom: !isDesktop
              ? 'calc(56px + env(safe-area-inset-bottom, 0px))'
              : 0,
          }}
        >
          {/*
            Compact density shrinks the in-page UI to ~88%. Applied to a
            wrapper INSIDE main, not main itself — `zoom` reduces an
            element's layout dimensions, so applying it to a `flex: 1`
            child shrinks main's actual height and leaves a visible gap
            below. The wrapper keeps main full-height and only the screen
            content scales. Comfortable density (default) renders with no
            transform.
          */}
          {/* One boundary, inside the chrome — nav and header stay put
           * while a screen's chunk arrives, so a slow network reads as
           * "the page is loading" rather than "the app went away".
           *
           * Keyed by route so navigating away from a screen whose chunk
           * failed resets it; without the key, one bad chunk would poison
           * every navigation after it. */}
          <div style={{ zoom: density.body < 14 ? 0.88 : 1 }}>
            <ScreenBoundary key={route} theme={theme} t={t}>
              {content}
            </ScreenBoundary>
          </div>
        </main>
        {!isDesktop && tabBar}
        {moreSheet}
      </div>
    </div>
  );
}

// ─── Route titles ────────────────────────────────────────────────────

// The leaderboard has no entry, like Live: it carries its own heading,
// and a top-bar title above it would say the same word twice.
const ROUTE_TITLES: Partial<Record<Route, (t: (k: string) => string) => string>> = {
  ideas: (t) => t('contentIdeas'),
  settings: (t) => t('settings'),
};
